import { Link } from 'react-router-dom';
import { Plane, Phone, Mail, MapPin, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  const navLinks = [
    { key: 'home', path: '/' },
    { key: 'about', path: '/ueber-uns' },
    { key: 'services', path: '/leistungen' },
    { key: 'pricing', path: '/preise' },
    { key: 'faq', path: '/faq' },
    { key: 'gallery', path: '/galerie' },
  ];

  return (
    <footer className="bg-[#0A0A0A] border-t border-white/5" data-testid="footer">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 bg-[#D4AF37] rounded-lg flex items-center justify-center">
                <Plane className="w-7 h-7 text-black" />
              </div>
              <span className="text-2xl font-bold tracking-tight">
                Parking<span className="text-[#D4AF37]">DUS</span>
              </span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">{t.footer.service24}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t.footer.navigation}</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {t.nav[link.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t.footer.contact}</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:+4915126777707"
                  className="flex items-start gap-3 text-white/60 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>+49 151 26777707</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:service@parkingdus.com"
                  className="flex items-start gap-3 text-white/60 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>service@parkingdus.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/60 text-sm">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Kieshecker Weg 141<br />
                    40474 Düsseldorf
                  </span>
                </div>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t.footer.legal}</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/impressum"
                  className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {t.footer.impressum}
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <Link
                  to="/kontakt"
                  className="text-white/60 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {t.footer.contact}
                </Link>
              </li>
            </ul>
            <div className="mt-8">
              <a
                href="https://mcdgroup.de/buchungssystem/index.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-[#D4AF37] text-black px-6 py-3 rounded-full font-semibold text-sm hover:bg-[#E5C34A] transition-all duration-300 gold-glow"
                data-testid="footer-book-btn"
              >
                {t.nav.bookNow}
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">
            © {currentYear} {t.footer.copyright}
          </p>
          <p className="text-white/40 text-sm">
            {t.footer.madeWith}{' '}
            <span className="text-[#D4AF37]">♥</span>
            {' '}{t.footer.inDusseldorf}{' '}
            <a 
              href="https://beta-webagentur.de" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#D4AF37] hover:underline"
            >
              Beta-Webagentur.de
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
